import { CloseButton, Combobox, Input, InputBase, useCombobox } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import AbsoluteError from 'components/ui/AbsoluteError';
import { FieldSelectStore } from 'stores/local/shared/form/FieldSelectStore';
import { useDevices } from 'utils/devices';
import { preventDefault } from 'utils/preventDefault';

import StatusUnique from '../FieldPhone/StatusUnique';

import OptionsDropdown from './OptionsDropdown';

import s from './FieldSelect.module.scss';

type FieldSelectProps = {
  className?: string;
  placeholder?: string;
  label?: string;
  size?: string;
  store: FieldSelectStore;
  fw?: string;
  placeholderClassName?: string;
  disabled?: boolean;
  optionsFooter?: (closeDropdown: () => void) => React.ReactNode;
};

const FieldSelect: React.FC<FieldSelectProps> = ({
  store,
  placeholder,
  label,
  className,
  fw = 500,
  size,
  placeholderClassName,
  disabled,
  optionsFooter,
}) => {
  const { isPhone } = useDevices();

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      store.blur();
      store.resetSearch();
    },

    onDropdownOpen: () => {
      store.focus();
      combobox.focusSearchInput();
    },
  });

  const onInputClick = React.useCallback(() => {
    combobox.toggleDropdown();
  }, [combobox]);

  const onOptionSubmit = React.useCallback(
    (value: string) => {
      store.setValueByKey(value);
      combobox.closeDropdown();
    },
    [store, combobox],
  );

  const onResetValue = React.useCallback(() => {
    store.resetValue();
    store.blur();
  }, [store]);

  const active = store.value?.[0] ?? null;

  if (!store.shown) {
    return null;
  }

  return (
    <div className={className}>
      <Combobox store={combobox} keepMounted={false} withinPortal={true} onOptionSubmit={onOptionSubmit}>
        <Combobox.Target>
          <Input.Wrapper
            withAsterisk={store.required}
            label={label}
            size={size ?? (isPhone ? 'md' : 'lg')}
            className={cn(s.wrapper, combobox.dropdownOpened ? s.dropdownOpened : '')}
          >
            {store.clientUniquenessStore && store.value.length > 0 && (
              <StatusUnique
                className={s.statusUnique}
                isUnique={store.clientUniquenessStore.isUnique.value}
                isLoading={store.clientUniquenessStore.loadingStageUnique?.isLoading ?? false}
              />
            )}
            <InputBase
              error={store.hasError && <AbsoluteError error={store.error} />}
              size={size ?? (isPhone ? 'md' : 'lg')}
              component="button"
              type="button"
              pointer
              onClick={onInputClick}
              disabled={disabled}
              data-dropdown-opened={combobox.dropdownOpened}
              rightSectionPointerEvents={active ? undefined : 'none'}
              rightSection={
                active ? (
                  <CloseButton size="lg" onMouseDown={preventDefault} onClick={onResetValue} disabled={disabled} />
                ) : (
                  <IconChevronDown />
                )
              }
              styles={{
                label: {
                  fontWeight: fw,
                },
                input: {
                  paddingLeft: 16,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            >
              {active?.value ??
                (placeholder && <Input.Placeholder className={placeholderClassName}>{placeholder}</Input.Placeholder>)}
            </InputBase>
          </Input.Wrapper>
        </Combobox.Target>

        <OptionsDropdown store={store} optionsFooter={optionsFooter?.(combobox.closeDropdown)} />
      </Combobox>
    </div>
  );
};

export default observer(FieldSelect);
